import { provideSingleton } from '@bcf-vanilla-ts-v1-shared/di/provide-singleton';
import type { RouteResolverParams } from '@bcf-vanilla-ts-v1-shared/misc/preload-store/route-resolver';
import { enrichOriginError } from '@bcf-vanilla-ts-v1-shared/misc/utils/enrich-origin-error';
import { attachErrorDiagnostics } from '@bcf-vanilla-ts-v1-shared/misc/utils/error-diagnostics';
import type { CryptoService } from '@bcf-vanilla-ts-v1-utilities/crypto-subtle/crypto.service';
import { provideCryptoService } from '@bcf-vanilla-ts-v1-utilities/crypto-subtle/crypto.service';
import { random } from 'rambdax-v11';
import type { Subscriber, Subscription } from 'rxjs';
import {
  filter,
  first,
  lastValueFrom,
  map,
  merge,
  NEVER,
  Observable,
  of,
  ReplaySubject,
  Subject,
  takeUntil
} from 'rxjs';
import type {
  BrowserSideMessage,
  InstanceCreatedMessage,
  SubscriptionCompleteMessage,
  SubscriptionErrorMessage,
  SubscriptionPayloadMessage
} from '../common/messages';
import {
  createInstanceMessage,
  destroyInstanceMessage,
  runPreloadersMessage,
  subjectNextMessage,
  subscribeToChannelMessage,
  unsubscribeFromChannelMessage,
  workerReadyAck
} from '../common/messages';
import type { ExtractedObservable, WorkerBlocChannels } from '../common/types';
import type { PreloaderId } from '../preloaders.type';
import type { WorkerBlocId } from '../worker-blocs.type';
import type { TypedSubject } from './types';
import type { WorkerReady } from './worker-ready';
import { provideWorkerReady } from './worker-ready';
import type { WorkerWrapper } from './worker-wrapper';
import { provideWorkerWrapper } from './worker-wrapper';

function filterIsCorrectMessage(message: Record<string, any>): message is BrowserSideMessage {
  return Boolean(message.action);
}

function isFunction(x: unknown): boolean {
  return Object.prototype.toString.call(x) == '[object Function]';
}

// Decision: see ADR-0002

export class BrowserSideBlocController<R extends WorkerBlocChannels> {
  private _subscribePayloadMessage$: Subject<SubscriptionPayloadMessage> = new Subject<SubscriptionPayloadMessage>();

  private _subscribeErrorMessage$: Subject<SubscriptionErrorMessage> = new Subject<SubscriptionErrorMessage>();

  private _subscribeCompleteMessage$: Subject<SubscriptionCompleteMessage> = new Subject<SubscriptionCompleteMessage>();

  private _instanceCreatedMessage$: Subject<InstanceCreatedMessage> = new Subject<InstanceCreatedMessage>();

  private _workerReadyForMessages$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  private _instanceCreatedUniqueIds: string[] = [];

  // uniqueIds that were destroyed, so a subTo/workerSubject().next() call arriving after
  // destroy (not just one already in flight, which _destroy$ + takeUntil covers) never posts
  // to a worker-side instance that no longer exists (WorkerBlocPushValueError).
  private _destroyedUniqueIds: Set<string> = new Set<string>();

  private _destroy$: Subject<string> = new Subject<string>();

  private _subscriptionSeq: number = 0;

  constructor(
    protected _worker: WorkerWrapper,
    protected _workerReady: WorkerReady
  ) {}

  public static getId(providedValue: WorkerBlocId): string {
    const cryptoService: CryptoService = provideCryptoService();
    return `${new Date().getTime()}~${cryptoService.randomUUID()}-${random(0, 1000)}~${providedValue}`;
  }

  public init(): void {
    this._listenForMessages();
  }

  private _listenForMessages(): void {
    this._worker
      .getMessage<BrowserSideMessage>()
      .pipe(filter(filterIsCorrectMessage))
      .subscribe((message: BrowserSideMessage) => {
        switch (message.action) {
          case 'workerReady':
            this._workerReadyForMessages$.next(true);
            this._workerReady.makeReady();
            this._worker.postMessage(workerReadyAck());
            break;
          case 'subscriptionPayload':
            this._subscribePayloadMessage$.next(message);
            break;
          case 'subscriptionError':
            this._subscribeErrorMessage$.next(message);
            break;
          case 'subscriptionComplete':
            this._subscribeCompleteMessage$.next(message);
            break;
          case 'instanceCreated':
            this._instanceCreatedUniqueIds.push(message.uniqueId);
            this._instanceCreatedMessage$.next(message);
            break;
        }
      });
  }

  public sendCreateInstance(workerBlocId: WorkerBlocId, uniqueId: string): void {
    // Subscribe synchronously (not `await lastValueFrom(...)`, which resumes on a microtask)
    // so a same-tick destroy can never overtake this uniqueId's own create - see
    // sendDestroyInstance() for why subscription order guarantees this.
    this._workerReadyForMessages$
      .pipe(first())
      .subscribe(() => this._worker.postMessage(createInstanceMessage(workerBlocId, uniqueId)));
  }

  // Decision: see ADR-0016.
  public async sendRunPreloaders(preloaderIds: PreloaderId[], params: RouteResolverParams, url: string): Promise<void> {
    await lastValueFrom(this._workerReadyForMessages$.pipe(first()));
    this._worker.postMessage(runPreloadersMessage(preloaderIds, params, url));
  }

  public workerBlocInitialized(workerBlocId: WorkerBlocId, uniqueId: string): Observable<void> {
    if (this._destroyedUniqueIds.has(uniqueId)) {
      return NEVER;
    }

    const alreadyCreated$: Observable<boolean> = of(this._instanceCreatedUniqueIds.includes(uniqueId)).pipe(
      filter((found: boolean) => found === true)
    );

    return merge(
      alreadyCreated$,
      this._instanceCreatedMessage$.pipe(
        first(
          (message: InstanceCreatedMessage) => message.workerBlocId === workerBlocId && message.uniqueId === uniqueId
        )
      )
    ).pipe(map(() => undefined));
  }

  public subTo<K extends keyof R['rx'], V extends ExtractedObservable<R['rx'][K]>>(
    workerBlocId: WorkerBlocId,
    uniqueId: string,
    channelRx: K
  ): Observable<V> {
    return new Observable((subscriber: Subscriber<V>) => {
      // The per-controller counter is what guarantees uniqueness; timestamp and random are only
      // belt-and-braces. `Date.now() + random()` alone collided when two subscriptions started in
      // the same millisecond and drew the same number.
      const subscriptionId: string = `${new Date().getTime()}~${++this._subscriptionSeq}~${random(0, 100000)}`;

      // Decision: channelName is part of the subscription identity, not decoration. Without it a
      // stray subId delivers channel A's payload to channel B's subscriber, which blows up
      // downstream as a wrong-shape payload (Sentry BCF-V2-PREVIEW-54, -4Y, -4X, -4W).
      const isCorrectMessage = <T extends BrowserSideMessage>(message: T): boolean =>
        message.action !== 'instanceCreated' &&
        (message as any)?.subId === subscriptionId &&
        (message as any).uniqueId === uniqueId &&
        (message as any).channelName === channelRx;

      const nextSub: Subscription = this._subscribePayloadMessage$
        .pipe(filter(isCorrectMessage))
        .subscribe((message: SubscriptionPayloadMessage) => subscriber.next(message.payload));

      const errorSub: Subscription = this._subscribeErrorMessage$
        .pipe(filter(isCorrectMessage))
        .subscribe((message: SubscriptionErrorMessage) => {
          const error: unknown = enrichOriginError(`WorkerBloc:${workerBlocId}#${String(channelRx)}`, message.error);
          attachErrorDiagnostics(error, {
            workerBloc: workerBlocId,
            channel: String(channelRx),
            phase: 'subscription'
          });
          subscriber.error(error);
        });

      const completeSub: Subscription = this._subscribeCompleteMessage$
        .pipe(filter(isCorrectMessage))
        .subscribe(() => subscriber.complete());

      const initSub: Subscription = this.workerBlocInitialized(workerBlocId, uniqueId)
        .pipe(takeUntil(this._destroy$.pipe(first((destroyUniqueId: string) => destroyUniqueId === uniqueId))))
        .subscribe(() =>
          this._worker.postMessage(subscribeToChannelMessage(workerBlocId, uniqueId, subscriptionId, channelRx))
        );

      return (): void => {
        initSub.unsubscribe();
        this._worker.postMessage(unsubscribeFromChannelMessage(workerBlocId, uniqueId, subscriptionId, channelRx));
        nextSub.unsubscribe();
        errorSub.unsubscribe();
        completeSub.unsubscribe();
      };
    });
  }

  public workerSubject<V extends TypedSubject<R['subjects'][K]>, K extends keyof R['subjects']>(
    workerBlocId: WorkerBlocId,
    uniqueIdOrFn: string | (() => string),
    subjectKey: K
  ): V {
    const resolveUniqueId = (uniqueIdOrFn: any): string => {
      if (isFunction(uniqueIdOrFn)) {
        return uniqueIdOrFn();
      }
      return uniqueIdOrFn;
    };
    return {
      next: (value: V) => {
        const resolvedUniqueId: string = resolveUniqueId(uniqueIdOrFn);
        this.workerBlocInitialized(workerBlocId, resolvedUniqueId)
          .pipe(takeUntil(this._destroy$.pipe(first((uniqueId: string) => uniqueId === resolvedUniqueId))))
          .subscribe(() => {
            this._worker.postMessage(subjectNextMessage(resolvedUniqueId, subjectKey, value));
          });
      }
    } as V;
  }

  public sendDestroyInstance(workerBlocId: WorkerBlocId, uniqueId: string): void {
    this._destroyedUniqueIds.add(uniqueId);
    this._destroy$.next(uniqueId);
    // gate on the same worker-ready signal sendCreateInstance uses, and subscribe synchronously
    // like it does, so the destroy postMessage can never overtake this uniqueId's own
    // createInstance postMessage on the worker's message queue - RxJS delivers a Subject's
    // value to subscribers in subscription order, and create always subscribes first.
    this._workerReadyForMessages$
      .pipe(first())
      .subscribe(() => this._worker.postMessage(destroyInstanceMessage(workerBlocId, uniqueId)));
  }
}

export function provideBrowserSideBlocController<T extends WorkerBlocChannels>(): BrowserSideBlocController<T> {
  return provideSingleton(
    BrowserSideBlocController,
    () => new BrowserSideBlocController<T>(provideWorkerWrapper(), provideWorkerReady())
  );
}
